import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "番剧库", to: "/library", cat: "all" },
  { label: "剧场版", to: "/library", cat: "剧场版" },
  { label: "OVA", to: "/library", cat: "OVA" },
  { label: "泡面番", to: "/library", cat: "泡面番" },
  { label: "国漫", to: "/library", cat: "国漫" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  const isActive = (item: (typeof NAV_ITEMS)[number]) => {
    if (item.to === "/") return location.pathname === "/";
    if (item.to === "/library") {
      if (location.pathname !== "/library") return false;
      const params = new URLSearchParams(location.search);
      const cat = params.get("cat") || "all";
      return cat === item.cat;
    }
    return location.pathname === item.to;
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/library?search=${encodeURIComponent(kw)}` : "/library");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-950/90 backdrop-blur-md border-b border-background-700/60 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex h-16 md:h-20 items-center justify-between gap-4">
          {/* logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-950">
              <i className="ri-heart-2-fill text-lg" />
            </span>
            <span
              className="font-heading text-xl md:text-2xl tracking-wide text-foreground-950"
            >
              樱花看番
            </span>
          </Link>

          {/* desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to === "/library" ? `${item.to}?cat=${item.cat}` : item.to}
                className={`rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap ${
                  isActive(item)
                    ? "bg-primary-500/15 text-primary-300"
                    : "text-foreground-700 hover:text-foreground-950 hover:bg-background-800/60"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {/* search */}
            <form onSubmit={onSearch} className="flex items-center">
              <div className="flex items-center gap-2 rounded-full border border-background-600 bg-background-800/70 px-3 py-1.5 focus-within:border-primary-500/60">
                <i className="ri-search-line text-sm text-foreground-500" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索番剧"
                  className="w-36 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-600 outline-none"
                />
              </div>
              <button
                type="submit"
                className="ml-2 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400"
              >
                搜索
              </button>
            </form>
          </div>

          {/* mobile toggle */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-background-600 text-foreground-950"
            aria-label="打开菜单"
          >
            <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {/* mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-background-700 bg-background-950/95 backdrop-blur-md px-4 py-4">
          <form onSubmit={onSearch} className="mb-4 flex items-center gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-full border border-background-600 bg-background-800/70 px-3 py-2">
              <i className="ri-search-line text-foreground-500" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索番剧"
                className="w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-600 outline-none"
              />
            </div>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to === "/library" ? `${item.to}?cat=${item.cat}` : item.to}
                className={`rounded-lg px-4 py-3 text-sm ${
                  isActive(item)
                    ? "bg-primary-500/15 text-primary-300"
                    : "text-foreground-700 hover:bg-background-800/60"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}